import BaseApi from './base.api';

// ==================== Request Types ====================

export interface PreScreeningAnswerRequest {
  questionId: number;
  answerText: string;
}

export interface PreparePaymentRequest {
  doctorId: number;
  preScreeningAnswers: PreScreeningAnswerRequest[];
  mainComplaint: string;
}

export interface CreateTeleChatRequest {
  doctorId: number;
  preScreeningAnswers: PreScreeningAnswerRequest[];
  mainComplaint: string;
  paymentIntentId: string;
}

export interface SendMessageRequest {
  messageText?: string;
  fileKey?: string;
  fileName?: string;
  fileType?: string;
  fileSizeBytes?: number;
}

export interface FileUploadRequest {
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface FileUploadResponse {
  uploadUrl: string;
  fileKey: string;
  fileName: string;
  contentType: string;
  maxSizeBytes: number;
}

export interface FileUploadConfig {
  allowedExtensions: string[];
  maxFileSizeBytes: number;
}

export interface PreparePaymentResponse {
  clientSecret: string;
  paymentIntentId: string;
  consultationPrice: number;
  followUp: boolean;
  discountPercent: number | null;
}

// ==================== Response Types ====================

export interface PreScreeningQuestionResponse {
  questionId: number;
  questionText: string;
  questionOrder: number;
  isActive: boolean;
}

export interface TeleChatListDoctorInfo {
  id: number;
  fullName: string;
  speciality: string;
}

export interface TeleChatListPatientInfo {
  id: number;
  fullName: string;
}

export interface TeleChatListResponse {
  chatId: number;
  status: 'PENDING_APPROVAL' | 'ACTIVE' | 'CLOSED' | 'DECLINED';
  paymentStatus: 'PENDING' | 'AUTHORIZED' | 'CAPTURED' | 'CANCELLED' | 'FAILED';
  consultationPrice: number;
  createdAt: string;
  lastMessageAt: string | null;
  unreadCount: number;
  lastMessage: string | null;
  // For patient view
  doctor?: TeleChatListDoctorInfo;
  // For doctor view
  patient?: TeleChatListPatientInfo;
}

export interface PreScreeningAnswerResponse {
  questionId: number;
  questionText: string;
  answerText: string;
}

export interface DoctorInfo {
  id: number;
  fullName: string;
  speciality: string;
  email: string;
}

export interface PatientInfo {
  id: number;
  fullName: string;
  age?: number;
  gender?: string;
  email: string;
}

export interface TeleChatDetailResponse {
  chatId: number;
  status: 'PENDING_APPROVAL' | 'ACTIVE' | 'CLOSED' | 'DECLINED';
  paymentStatus: 'PENDING' | 'AUTHORIZED' | 'CAPTURED' | 'CANCELLED' | 'FAILED';
  consultationPrice: number;
  createdAt: string;
  lastMessageAt: string | null;
  closedAt: string | null;
  declineReason: string | null;
  preScreeningAnswers: PreScreeningAnswerResponse[];
  // For patient view
  doctor?: DoctorInfo;
  // For doctor view
  patient?: PatientInfo;
}

export interface TeleChatMessageResponse {
  messageId: number;
  senderType: 'PATIENT' | 'DOCTOR' | 'SYSTEM';
  senderId: number | null;
  messageText: string | null;
  fileUrl: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  fileType: string | null;
  isPreScreening: boolean;
  createdAt: string;
}

export interface CreateTeleChatResponse {
  chatId: number;
}

export interface MonthlyStats {
  month: string;
  year: number;
  consultations: number;
  income: number;
}

export interface DoctorDashboardStatsResponse {
  activeChats: number;
  pendingChats: number;
  closedChatsThisMonth: number;
  totalChats: number;
  incomeThisMonth: number;
  totalIncome: number;
  totalPatients: number;
  newPatientsThisMonth: number;
  monthlyStats: MonthlyStats[];
}

// ==================== API Class ====================

class TeleChatApi extends BaseApi {
  constructor() {
    super();
  }

  // ==================== Patient Endpoints ====================

  /**
   * Get all chats for the current patient.
   */
  getPatientChats = (): Promise<TeleChatListResponse[]> => {
    return this.get('/patient/chats');
  };

  /**
   * Get chat details for patient.
   */
  getPatientChatDetail = (chatId: number): Promise<TeleChatDetailResponse> => {
    return this.get(`/patient/chats/${chatId}`);
  };

  /**
   * Prepare payment for a new chat (creates Stripe PaymentIntent but not the chat).
   * Call this before showing the payment form.
   */
  preparePayment = (request: PreparePaymentRequest): Promise<PreparePaymentResponse> => {
    return this.post('/patient/chats/prepare-payment', request);
  };

  /**
   * Create a new chat request with a doctor (after successful payment).
   * The request must include the paymentIntentId from preparePayment.
   */
  createChat = (request: CreateTeleChatRequest): Promise<CreateTeleChatResponse> => {
    return this.post('/patient/chats', request);
  };

  /**
   * Get messages for a patient's chat.
   */
  getPatientMessages = (chatId: number): Promise<TeleChatMessageResponse[]> => {
    return this.get(`/patient/chats/${chatId}/messages`);
  };

  /**
   * Send a message as patient.
   */
  sendPatientMessage = (
    chatId: number,
    request: SendMessageRequest
  ): Promise<TeleChatMessageResponse> => {
    return this.post(`/patient/chats/${chatId}/messages`, request);
  };

  /**
   * Mark chat as read by patient.
   */
  markPatientChatRead = (chatId: number): Promise<{ success: boolean }> => {
    return this.post(`/patient/chats/${chatId}/mark-read`);
  };

  /**
   * Cancel a pending chat request.
   */
  cancelChat = (chatId: number): Promise<{ success: boolean; message: string }> => {
    return this.delete(`/patient/chats/${chatId}`);
  };

  /**
   * Get pre-screening questions.
   */
  getPreScreeningQuestions = (): Promise<PreScreeningQuestionResponse[]> => {
    return this.get('/patient/chats/pre-screening-questions');
  };

  /**
   * Get Stripe configuration (publishable key).
   */
  getStripeConfig = (): Promise<{ publishableKey: string }> => {
    return this.get('/patient/chats/stripe-config');
  };

  /**
   * Get file upload configuration for patient.
   */
  getPatientFileUploadConfig = (): Promise<FileUploadConfig> => {
    return this.get('/patient/chats/file-upload-config');
  };

  /**
   * Get presigned upload URL for patient.
   */
  getPatientUploadUrl = (
    chatId: number,
    request: FileUploadRequest
  ): Promise<FileUploadResponse> => {
    return this.post(`/patient/chats/${chatId}/upload-url`, request);
  };

  /**
   * Get presigned download URL for patient.
   */
  getPatientDownloadUrl = (
    chatId: number,
    fileKey: string
  ): Promise<{ downloadUrl: string }> => {
    return this.get(`/patient/chats/${chatId}/files/download-url?fileKey=${encodeURIComponent(fileKey)}`);
  };

  // ==================== Doctor Endpoints ====================

  /**
   * Get dashboard statistics for the current doctor.
   */
  getDoctorDashboardStats = (): Promise<DoctorDashboardStatsResponse> => {
    return this.get('/doctor/chats/dashboard-stats');
  };

  /**
   * Get all chats for the current doctor.
   */
  getDoctorChats = (
    statuses?: string[],
    search?: string
  ): Promise<TeleChatListResponse[]> => {
    const params = new URLSearchParams();
    if (statuses && statuses.length > 0) {
      statuses.forEach(s => params.append('statuses', s));
    }
    if (search) {
      params.append('search', search);
    }
    const queryString = params.toString();
    return this.get(`/doctor/chats${queryString ? '?' + queryString : ''}`);
  };

  /**
   * Get chat details for doctor.
   */
  getDoctorChatDetail = (chatId: number): Promise<TeleChatDetailResponse> => {
    return this.get(`/doctor/chats/${chatId}`);
  };

  /**
   * Approve a pending chat request.
   */
  approveChat = (chatId: number): Promise<{ success: boolean; message: string }> => {
    return this.post(`/doctor/chats/${chatId}/approve`);
  };

  /**
   * Decline a pending chat request.
   */
  declineChat = (
    chatId: number,
    reason?: string
  ): Promise<{ success: boolean; message: string }> => {
    return this.post(`/doctor/chats/${chatId}/decline`, reason ? { reason } : {});
  };

  /**
   * Get messages for a doctor's chat.
   */
  getDoctorMessages = (chatId: number): Promise<TeleChatMessageResponse[]> => {
    return this.get(`/doctor/chats/${chatId}/messages`);
  };

  /**
   * Send a message as doctor.
   */
  sendDoctorMessage = (
    chatId: number,
    request: SendMessageRequest
  ): Promise<TeleChatMessageResponse> => {
    return this.post(`/doctor/chats/${chatId}/messages`, request);
  };

  /**
   * Mark chat as read by doctor.
   */
  markDoctorChatRead = (chatId: number): Promise<{ success: boolean }> => {
    return this.post(`/doctor/chats/${chatId}/mark-read`);
  };

  /**
   * Close an active chat.
   */
  closeChat = (
    chatId: number,
    closingNotes?: string
  ): Promise<{ success: boolean; message: string }> => {
    return this.post(`/doctor/chats/${chatId}/close`, closingNotes ? { closingNotes } : {});
  };

  /**
   * Get file upload configuration for doctor.
   */
  getDoctorFileUploadConfig = (): Promise<FileUploadConfig> => {
    return this.get('/doctor/chats/file-upload-config');
  };

  /**
   * Get presigned upload URL for doctor.
   */
  getDoctorUploadUrl = (
    chatId: number,
    request: FileUploadRequest
  ): Promise<FileUploadResponse> => {
    return this.post(`/doctor/chats/${chatId}/upload-url`, request);
  };

  /**
   * Get presigned download URL for doctor.
   */
  getDoctorDownloadUrl = (
    chatId: number,
    fileKey: string
  ): Promise<{ downloadUrl: string }> => {
    return this.get(`/doctor/chats/${chatId}/files/download-url?fileKey=${encodeURIComponent(fileKey)}`);
  };

  // ==================== Utility Methods ====================

  /**
   * Upload a file to S3 using presigned URL.
   * Returns the fileKey to use when sending a message.
   */
  uploadFile = async (
    chatId: number,
    file: File,
    isDoctor: boolean
  ): Promise<{ fileKey: string; fileName: string; fileType: string; fileSizeBytes: number }> => {
    // Get presigned URL
    const uploadRequest: FileUploadRequest = {
      fileName: file.name,
      contentType: file.type,
      fileSize: file.size,
    };

    const uploadResponse = isDoctor
      ? await this.getDoctorUploadUrl(chatId, uploadRequest)
      : await this.getPatientUploadUrl(chatId, uploadRequest);

    // Upload to S3. Only Content-Type is signed; the browser sets Content-Length
    // itself (setting it manually is ignored, and signing it causes a 403).
    const response = await fetch(uploadResponse.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error('Failed to upload file to S3');
    }

    return {
      fileKey: uploadResponse.fileKey,
      fileName: file.name,
      fileType: file.type,
      fileSizeBytes: file.size,
    };
  };

  /**
   * Get a download URL for a file.
   */
  getDownloadUrl = async (
    chatId: number,
    fileKey: string,
    isDoctor: boolean
  ): Promise<string> => {
    const response = isDoctor
      ? await this.getDoctorDownloadUrl(chatId, fileKey)
      : await this.getPatientDownloadUrl(chatId, fileKey);
    return response.downloadUrl;
  };
}

export const teleChatApi = new TeleChatApi();
export default teleChatApi;
